// equivalencies/EquivalencyPanel.js
// Main equivalency panel component with dual-column layout

import React, { useState, useMemo, useCallback } from 'react';
import EquivalencyCard from './EquivalencyCard.js';
import { calculateEquivalencies, generateFactorsText } from './formulas.js';
import './styles.css';

/**
 * EquivalencyPanel component
 * Main panel displaying environmental equivalencies in a dual-column layout
 * 
 * @param {Object} props
 * @param {Object} [props.totals] - Totals from parent component { mmbtu, mtco2e }
 * @param {boolean} [props.showFactors] - Whether to show the factors sidebar expanded by default
 */
const EquivalencyPanel = ({ totals, showFactors = false }) => {
  // Local state for testing when no props provided
  const [localTotals, setLocalTotals] = useState({
    mmbtu: 0.0169,
    mtco2e: 0.0042
  });

  // Sidebar state management
  const [isFactorsExpanded, setIsFactorsExpanded] = useState(showFactors);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // Use props totals if provided, otherwise use local state
  const activeTotals = totals || localTotals;
  const showLocalInput = !totals;

  // Calculate equivalencies
  const equivalencies = useMemo(() => {
    return calculateEquivalencies(activeTotals);
  }, [activeTotals]);

  // Handle local input changes
  const handleLocalInputChange = useCallback((field, value) => {
    const numValue = parseFloat(value) || 0;
    setLocalTotals(prev => ({
      ...prev,
      [field]: numValue
    }));
  }, []);

  // Toggle sidebar expansion
  const handleToggleFactors = useCallback(() => {
    setIsFactorsExpanded(prev => !prev);
  }, []);

  // Copy factors to clipboard with success message
  const handleCopyFactors = useCallback(async () => {
    try {
      const factorsText = generateFactorsText();
      await navigator.clipboard.writeText(factorsText);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy factors:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generateFactorsText();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    }
  }, []);

  // Check if we have any meaningful totals to display
  const hasTotals = activeTotals && (
    (activeTotals.mmbtu != null && Math.abs(activeTotals.mmbtu) > 0) ||
    (activeTotals.mtco2e != null && Math.abs(activeTotals.mtco2e) > 0)
  );

  // If no totals and using props mode, don't render
  if (!showLocalInput && !hasTotals) {
    return null;
  }

  return (
    <div className="equiv-layout">
      {/* Main Panel (Left) */}
      <div className="equiv-main">
        <div className="equivalency-header">
          <h3 className="equivalency-title">Environmental Equivalencies</h3>
          <p className="equivalency-subtitle">
            Visual representation of environmental impacts in familiar terms
          </p>
        </div>

        {showLocalInput && (
          <div className="equivalency-local-input">
            <h4>Test Input (Development Mode)</h4>
            <div className="local-input-row">
              <label>
                Million BTU:
                <input
                  type="number"
                  step="0.0001"
                  value={localTotals.mmbtu}
                  onChange={(e) => handleLocalInputChange('mmbtu', e.target.value)}
                  className="local-input"
                />
              </label>
              <label>
                MTCO₂E:
                <input
                  type="number"
                  step="0.0001"
                  value={localTotals.mtco2e}
                  onChange={(e) => handleLocalInputChange('mtco2e', e.target.value)}
                  className="local-input"
                />
              </label>
            </div>
          </div>
        )}

        <div className="equivalency-grid">
          {/* Energy Column (Left) */}
          <div className="equivalency-column energy-column">
            <h4 className="column-header">
              <span className="column-icon">⚡</span>
              Energy Equivalents
            </h4>
            <EquivalencyCard
              type="gasoline"
              value={equivalencies.gasoline.value}
              rawValue={equivalencies.gasoline.rawValue}
              isBenefit={equivalencies.gasoline.isBenefit}
            />
            <EquivalencyCard
              type="plasticBottles"
              value={equivalencies.plasticBottles.value}
              rawValue={equivalencies.plasticBottles.rawValue}
              isBenefit={equivalencies.plasticBottles.isBenefit}
            />
          </div>

          {/* GHG Column (Right) */}
          <div className="equivalency-column ghg-column">
            <h4 className="column-header">
              <span className="column-icon">🌍</span>
              GHG Equivalents
            </h4>
            <EquivalencyCard
              type="aluminumCans"
              value={equivalencies.aluminumCans.value}
              rawValue={equivalencies.aluminumCans.rawValue}
              isBenefit={equivalencies.aluminumCans.isBenefit}
            />
            <EquivalencyCard
              type="treeSeedlings"
              value={equivalencies.treeSeedlings.value}
              rawValue={equivalencies.treeSeedlings.rawValue}
              isBenefit={equivalencies.treeSeedlings.isBenefit}
            />
          </div>
        </div>
      </div>

      {/* Independent Sidebar (Right) 
      <div className="equiv-aside">
        <h4 className="aside-title">Methodology / Factors</h4>
        
        <div className="aside-head">
          <button
            onClick={handleCopyFactors}
            className="copy-factors-button"
            title="Copy conversion factors and formulas to clipboard"
          >
            {showCopySuccess ? '✅ Copied!' : '📋 Copy Factors'}
          </button>
          <button
            onClick={handleToggleFactors}
            className="toggle-details-button"
            title="Toggle methodology details"
          >
            {isFactorsExpanded ? '▲ Hide Details' : '▼ Show Details'}
          </button>
        </div>

        <div className="aside-body" hidden={!isFactorsExpanded}>
          <div className="methodology-content">
            <p><strong>Environmental Equivalency Conversion Factors:</strong></p>
            <p></p>
            <p><strong>Energy Conversions:</strong></p>
            <p>• Gasoline: 120,214 Btu/gallon</p>
            <p>• Plastic Bottles: 1,228 Btu/bottle</p>
            <p></p>
            <p><strong>GHG Conversions:</strong></p>
            <p>• Aluminum Cans: 98.7 g CO2e avoided per can recycled</p>
            <p>• Tree Seedlings: 0.6 metric tons CO2 sequestered per tree over 10 years</p>
            <p></p>
            <p><strong>Formulas:</strong></p>
            <p>• Gallons = |MMBtu| × 1,000,000 ÷ 120,214</p>
            <p>• Bottles = |MMBtu| × 1,000,000 ÷ 1,228</p>
            <p>• Cans = |MTCO2e| × 1,000,000 ÷ 98.7</p>
            <p>• Trees = |MTCO2e| ÷ 0.6</p>
            <p></p>
            <p><strong>Note:</strong> All calculations use absolute values to show environmental impact magnitude.</p>
            <p></p>
            <p>Values shown are absolute magnitudes. Green "Benefit" badges indicate environmental benefits (negative impacts).</p>
            <p></p>
           
            
          </div>
        </div>
      </div>*/}
    </div>
  );
};

export default EquivalencyPanel;
